/**
 * SeamRegimesPanel — the four canonical seam regimes (R1–R4).
 *
 * Renders each regime as a card with its governance characteristics and
 * its deterministic required-witness tuple (RD-27 §2, §5).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import regimesData from "../data/regimes.json" with { type: "json" };
const REGIME_TOKENS: Record<string, string> = {
  regimeTool: panelTokens.regimeTool,
  regimeSubAgent: panelTokens.regimeSubAgent,
  regimeExternal: panelTokens.regimeExternal,
  regimePersistent: panelTokens.regimePersistent,
};
export function SeamRegimesPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Seam Regimes (R1–R4)"
      subtitle="Each agentic boundary crossing belongs to one of four canonical regimes, each with a deterministic required-witness tuple."
      accentColor={panelTokens.regimeTool}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          ABK recognizes four canonical seam regimes. Each has a distinct
          governance topology. The required-witness tuple for a regime is the
          set of witnesses that must be exhibited for a crossing in that regime
          to be structurally compliant — absent any of them, the crossing is
          unconstructable.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.PanelGrid minColumnWidth={340} gap={12}>
        {regimesData.regimes.map((r) => {
          const color = REGIME_TOKENS[r.token] ?? panelTokens.accent;
          return (
            <PanelSurface.AccentCard
              key={r.id}
              symbol={r.id}
              title={r.name}
              accent={color}
              footer={
                <>
                  <PanelSurface.SectionLabel>
                    Required witness tuple
                  </PanelSurface.SectionLabel>
                  <PanelSurface.BadgeGroup
                    items={[
                      ...r.required.map((w) => ({
                        id: w,
                        label: w,
                        color,
                        title: `${w} required for ${r.name}`,
                      })),
                      ...r.conditional.map((c) => ({
                        id: `${c.witness}:${c.when}`,
                        label: `${c.witness} (${c.when})`,
                        color: panelTokens.muted,
                        title: `conditionally required: ${c.when}`,
                      })),
                    ]}
                  />
                </>
              }
            >
              <div className="mb-3 text-[13px] leading-[1.6] text-[var(--abk-text)]">
                {r.summary}
              </div>

              <PanelSurface.SectionLabel className="mb-1">
                Governance characteristics
              </PanelSurface.SectionLabel>
              <PanelSurface.BulletedList
                items={r.characteristics}
                className="mt-1 mb-3"
                compact
              />
            </PanelSurface.AccentCard>
          );
        })}
      </PanelSurface.PanelGrid>

      <PanelSurface.SectionHeading>
        The Required Witness Tuple table (deterministic)
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>{`-- required-witnesses : SeamRegime → List Witness   (total, finite)
R1 Tool Invocation        → Permit, Budget, TransportedCtx  (+ Fresh if stateful)
R2 Sub-agent Delegation   → Permit, Delegation, Precommit, Fuel
R3 External Information   → TransportedCtx, Declassify       (+ Permit if access-controlled)
R4 Persistent Store       → Budget, TransportedCtx           (+ Release if delete)

-- ABK-4 rwt-sufficient : AllWitnessesPresent r (required-witnesses r) → SeamCompliant r`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-27 §2 Seam regimes",
            path: "research/settled/RD-27-AGENTIC-BOUNDARY-KERNEL.md",
          },
          {
            label: "SeamObligations.agda",
            path: "agda/Research/AgenticBoundaryKernel/SeamObligations.agda",
          },
          {
            label: "required-witness-tuples.md",
            path: "research/agentic-boundary-kernel/required-witness-tuples.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
